import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// Lodash
import isEmpty from 'lodash/isEmpty';
import reverse from 'lodash/reverse';
import has from 'lodash/has';

// Date-fns
import Format from 'date-fns/format';

// Multibrand-UI Components
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import StyledContainer from '@zg-rentals/multibrand-ui/lib/StyledContainer';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import Tooltip from '@zg-rentals/multibrand-ui/lib/Tooltip';

// TODO: update mui Row, Col to use zrs-comet
import { Container } from '@zillow/zrs-comet';

// Actions
import StatisticsActions from 'app/shared/actions/StatisticsActions';

import AsideAnalytics from 'app/shared/components/AsideAnalytics';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import sharedConfig from 'app/shared/sharedConfig';
import PDPContentSkeleton from 'app/shared/components/skeletons/PDPContentSkeleton';
import Table from 'app/shared/components/Table';

// Selectors
import { UserSelectors } from 'app/shared/selectors';

import pathsManifest from 'app/shared/pathsManifest';

// Stylesheets
import './style.scss';

const DEFAULT_PAGE_TITLE = 'All Your Listings';

const propTypes = {
    match: PropTypes.object.isRequired,
};

class StatisticsPage extends React.Component {
    constructor(props) {
        super(props);

        this.state = this.getInitialState();

        this.getStatisticsData = this.getStatisticsData.bind(this);
        this.renderLoadComplete = this.renderLoadComplete.bind(this);
        this.renderStatsTables = this.renderStatsTables.bind(this);
        this.renderHeaders = this.renderHeaders.bind(this);
        this.getTableData = this.getTableData.bind(this);
    }

    getInitialState() {
        return {
            isLoading: false,
            pageTitle: DEFAULT_PAGE_TITLE,
            statisticsData: {},
            listingId: this.props.match.params.propertyId, // endpoint for stats is listingId
        };
    }

    componentWillMount() {
        this.setState({ isLoading: true });
    }

    componentDidMount() {
        this.getStatisticsData(this.state.listingId);
    }

    getStatisticsData(listingId) {
        const params = {};

        if (listingId) {
            params.listingId = listingId;

            this.setState({ listingId });
        }

        this.props.dispatch(StatisticsActions.getStats(params))
            .then((result) => {
                const data = result.payload;

                if (data.success === false) {
                    this.setState({
                        isLoading: false,
                        error: data.error.message,
                    });
                } else {
                    if (data.listingTitle) {
                        this.setState({
                            pageTitle: `${data.listingTitle}`,
                        });
                    } else if (data.singleListing) {
                        this.setState({
                            pageTitle: 'Your Listing',
                        });
                    }

                    this.setState({
                        isLoading: false,
                        statisticsData: data,
                    });
                }
            })
            .catch((err) => {
                global.TEMPORARY_global_logger.error(err);
            });
    }

    getHeaderList(date, hasBillableData) {
        const headerList = hasBillableData ? [
            date,
            'Listing Views',
            'Phone Leads',
            'Email Leads',
            'Total Leads',
            'Unique Clicks',
            'Unique Leads',
        ] : [
            date,
            'Listing Views',
            'Phone Leads',
            'Email Leads',
            'Total Leads',
        ];

        const toolTipMap = hasBillableData ? {
            'Unique Clicks': 'Number of unique clicks this property received in a day',
            'Unique Leads': 'Number of unique leads this property received in a day',
        } : {};

        return headerList.map((headerItem, index) => (
            <th key={index} className="Table-HeaderCell">
                {
                    has(toolTipMap, headerItem) ?
                        this.getToolTipHeader(headerItem, toolTipMap[headerItem]) :
                        headerItem
                }
            </th>
        ));
    }

    getToolTipHeader(headerItem, toolTipMessage) {
        return (
            <div>
                <Tooltip
                    trigger="hover"
                    position="bottom-start"
                    type="primary"
                    targetClass="Unique-header-tooltip-target"
                >
                    <Tooltip.Target>
                        {headerItem}
                    </Tooltip.Target>
                    <div>
                        {toolTipMessage}
                    </div>
                </Tooltip>
            </div>
        );
    }

    getRowList(data, isDailyTable, hasBillableData) {
        const statistics = Object.values(data);
        return reverse(statistics).map((stat) => {
            // Month data is in 'MM-YYYY' format and date-fns doesn't know how to process it.
            // Add a fake day to the month-year string so we can format the date string
            const monthDayYear = `${stat.displayedDate.slice(0, 3)}01-${stat.displayedDate.slice(3)}`;
            // @TODO Temporary fix: date-fns needs ISO-8601 strings
            // Waiting for backend to return a date object or ISO-8601 string
            const monthDayYearToShuffle = isDailyTable ? stat.displayedDate : monthDayYear;
            const monthDayYearArray = monthDayYearToShuffle.split('-');
            const yearMonthDay = [monthDayYearArray[2], monthDayYearArray[0], monthDayYearArray[1]].join('-');
            const format = isDailyTable ? 'MMM DD' : 'MMM YY';
            const date = Format(yearMonthDay, format);

            const rowData = hasBillableData ? {
                date,
                listingViewed: stat.totalViewed,
                phoneLeads: stat.totalPhoneLeads,
                emailLeads: stat.totalInquiries,
                totalLeads: stat.totalPhoneLeads + stat.totalInquiries,
                uniqueClicks: stat.totalBillableClicks < 0 ? 0 : stat.totalBillableClicks,
                uniqueLeads: stat.totalBillableLeads < 0 ? 0 : stat.totalBillableLeads,
            } : {
                date,
                listingViewed: stat.totalViewed,
                phoneLeads: stat.totalPhoneLeads,
                emailLeads: stat.totalInquiries,
                totalLeads: stat.totalPhoneLeads + stat.totalInquiries,
            };

            return (
                <tr key={rowData.date}>
                    {
                        Object.keys(rowData).map(key => (
                            <StyledContainer
                                htmlTag="td"
                                borderBottom={true}
                                className="Table-BodyRow"
                                key={key}
                            >
                                {rowData[key]}
                            </StyledContainer>
                        ))
                    }
                </tr>
            );
        });
    }

    getFooterList(data, hasBillableData) {
        const rowData = hasBillableData ? {
            date: 'Past 31 days',
            listingViewed: data.totalViewed,
            phoneLeads: data.totalPhoneLeads,
            emailLeads: data.totalInquiries,
            totalLeads: data.totalPhoneLeads + data.totalInquiries,
            uniqueClicks: data.totalBillableClicks < 0 ? 0 : data.totalBillableClicks,
            uniqueLeads: data.totalBillableLeads < 0 ? 0 : data.totalBillableLeads,
        } : {
            date: 'Past 31 days',
            listingViewed: data.totalViewed,
            phoneLeads: data.totalPhoneLeads,
            emailLeads: data.totalInquiries,
            totalLeads: data.totalPhoneLeads + data.totalInquiries,
        };

        return [(
            <tr key="1">
                {
                    Object.keys(rowData).map(key => (
                        <StyledContainer
                            borderBottom={true}
                            htmlTag="td"
                            className="Table-BodyCell"
                            key={key}
                        >
                            {rowData[key]}
                        </StyledContainer>
                    ))
                }
            </tr>
        )];
    }

    getTableData(statisticModel, attribute, isDailyTable, showMonthlySummary, hasBillableData) {
        const tableData = {
            headerList: this.getHeaderList(isDailyTable ? 'Date' : 'Month', hasBillableData),
            rowList: this.getRowList(statisticModel[attribute], isDailyTable, hasBillableData),
            errorMessage: 'There were no traffic records found for your listings.' +
            ' They may take around an hour to show up here.',
        };

        if (showMonthlySummary) {
            tableData.footerList = this.getFooterList(statisticModel.dailyListingStats, hasBillableData);
        }

        return tableData;
    }

    renderHeaders(dailyListingStats) {
        return (
            <div className="StatisticsPage-header">
                <div className="StatisticsPage-header-item">
                    <Text type="h4">
                        {`${dailyListingStats.totalInquiries + dailyListingStats.totalPhoneLeads} total leads`}
                    </Text>
                    <Text type="h5">
                        Past 31 days
                    </Text>
                </div>

                <div className="StatisticsPage-header-item">
                    <Text type="h4">
                        {`${dailyListingStats.totalViewed} total views`}
                    </Text>
                    <Text type="h5">
                        Past 31 days
                    </Text>
                </div>
            </div>
        );
    }

    renderStatsTables() {
        // statistic model
        const statisticModel = this.state.statisticsData;
        const { dailyListingStats } = statisticModel;
        const isEmptyDaily = isEmpty(statisticModel.dailyListingStatsByYyyyMmDd);
        const isEmptyMonthly = isEmpty(statisticModel.monthlyListingStatsByYyyyMm);
        const hasDailyBillableData = statisticModel.dailyBillableCountsAvailable;
        const hasMonthlyBillableData = statisticModel.monthlyBillableCountsAvailable;
        const monthlySummary = statisticModel && !isEmptyDaily;

        return (
            <div className="StatisticsPage-tables-container">
                { monthlySummary ? this.renderHeaders(dailyListingStats) : null }

                <div className="StatisticsPage-table-container">
                    <Text type="h5" className="StatisticsPage-table-title">
                        Events by Day
                    </Text>
                    <Table
                        tableData={
                            this.getTableData(statisticModel,
                                'dailyListingStatsByYyyyMmDd',
                                true,
                                monthlySummary,
                                hasDailyBillableData)}
                        className="StatisticsPage-table-daily"
                    />
                </div>

                { !statisticModel.monthlySummariesUnavailable && !isEmptyMonthly ? (
                    <div className="StatisticsPage-table-container">
                        <Text type="h5" className="StatisticsPage-table-title">
                            Events by Month
                        </Text>
                        <Table
                            tableData={
                                this.getTableData(statisticModel,
                                    'monthlyListingStatsByYyyyMm',
                                    false,
                                    false,
                                    hasMonthlyBillableData)}
                            className="StatisticsPage-table-monthly"
                        />
                    </div>
                ) : null }
            </div>
        );
    }

    renderLoadComplete() {
        const { hideAsideAnalytics } = this.props;
        const paths = pathsManifest.getAllPaths();

        const listingIdParam = this.state.listingId ? `?listingId=${this.state.listingId}` : '';
        const excelPath = paths.exportExcel.statistics;
        const excelUrl = `${paths.proxy}/${sharedConfig.baseApi}${excelPath}${listingIdParam}`;

        return (
            <Container>
                <Row>
                    <Col xs={12} md={9}>
                        {this.state.error
                            ? (
                                <Text className="error">
                                    {this.state.error}
                                </Text>
                            ) : this.renderStatsTables()
                        }
                    </Col>

                    {!this.state.error && (
                        <Col xs={12} md={3}>
                            {!hideAsideAnalytics && (
                                <AsideAnalytics
                                    buttonText="Download Stats"
                                    excelUrl={excelUrl}
                                />
                            )}
                        </Col>
                    )}
                </Row>
            </Container>
        );
    }

    render() {
        const pageTitleContent = `Statistics for ${this.state.pageTitle}`;

        return (
            <div className="StatisticsPage">
                <HeadWrapper
                    title={pageTitleContent}
                />
                {this.state.isLoading ?
                    (
                        <PDPContentSkeleton />
                    ) :
                    this.renderLoadComplete()
                }
            </div>
        );
    }
}

const mapStateToProps = state => ({
    user: UserSelectors.makeUser(state),
});

StatisticsPage.propTypes = propTypes;

export { StatisticsPage };
export default connect(mapStateToProps)(StatisticsPage);
